
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage } from '@/types/group';
import { toast } from 'sonner';
import { User } from '@supabase/supabase-js';

interface UseChatMessagesProps {
  tripId: string;
  user: User | null;
  username: string;
}

export const useChatMessages = ({ tripId, user, username }: UseChatMessagesProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  
  // Fetch chat messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('trip_id', tripId)
          .order('created_at', { ascending: true });
          
        if (error) throw error;
        
        setMessages(data || []);
      } catch (error) {
        console.error('Error fetching messages:', error);
        toast.error('Failed to load chat messages');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMessages();
  }, [tripId]);
  
  // Setup realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('chat-changes')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'chat_messages',
        filter: `trip_id=eq.${tripId}`
      }, (payload) => {
        setMessages(current => [...current, payload.new as ChatMessage]);
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [tripId]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    if (!user && !username.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    setIsSending(true);
    
    try {
      // Get the name to display
      const displayName = user ? (user.user_metadata?.username || 'User') : username;
      
      // Send the message
      await supabase
        .from('chat_messages')
        .insert({
          trip_id: tripId,
          user_id: user?.id || null,
          user_name: displayName,
          message: newMessage.trim()
        });
      
      // Clear the message input
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  return {
    messages,
    newMessage,
    setNewMessage,
    isLoading,
    isSending,
    handleSendMessage
  };
};
